import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { Target, Users, Wrench, BarChart3, ClipboardCheck, Eye, Handshake } from "lucide-react";

const principles = [
  {
    icon: Eye,
    title: "Kundförståelse",
    text: "Med en helhetssyn förstå våra kunders verksamhet, krav, behov och förväntningar.",
  },
  {
    icon: Target,
    title: "Rätt tid, rätt kvalitet",
    text: "Våra produkter anpassas till kundens krav och behov samt levereras på rätt sätt i rätt tid.",
  },
  {
    icon: BarChart3,
    title: "Ständiga förbättringar",
    text: "Arbeta med ständiga förbättringar och förebyggande insatser.",
  },
  {
    icon: Users,
    title: "Kompetenta medarbetare",
    text: "Medarbetarna är kompetenta och medvetna om betydelsen av sina insatser baserat på konceptet: Rätt från mig!",
  },
  {
    icon: Wrench,
    title: "Effektiva hjälpmedel",
    text: "Medarbetarna stöds av effektiva hjälpmedel i form av metoder, verktyg, stödsystem och utrustning samt utbildning.",
  },
  {
    icon: ClipboardCheck,
    title: "Uppföljning",
    text: "Löpande följa upp uppnådd kvalitet, samlade resultat och ständigt förbättra verksamheten.",
  },
  {
    icon: Handshake,
    title: "Mål i harmoni",
    text: "Utforma mål och handlingsplaner som är i harmoni med denna policy och intressenters krav och förväntningar.",
  },
];

export default function Kvalitetspolicy() {
  return (
    <Layout>
      <SEOHead
        title="Kvalitetspolicy | ASPAB"
        description="Läs om ASPAB:s kvalitetspolicy. Vi levererar produkter med målsättning mot noll fel, i rätt tid och med ständiga förbättringar."
        canonical="https://aspab.com/kvalitetspolicy"
        path="/kvalitetspolicy"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Kvalitetspolicy", href: "/kvalitetspolicy" },
        ]}

        ogImage="/images/og-kvalitetspolicy_53cc4939.png"
      />

      <PageHero
        title="Kvalitetspolicy"
        subtitle="Produkter ska levereras i rätt tid och produceras med en målsättning mot noll fel."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Kvalitetspolicy", href: "/kvalitetspolicy" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            <div className="space-y-12">

              {/* Inledning */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Vår kvalitetsfilosofi
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Aspab AB tillverkar produkter till kunder med högt ställda krav och förväntningar.
                  Verksamheten och dess processer ska utformas och bedrivas så att våra intressenter
                  erhåller ett mervärde genom att samarbeta och vara delaktiga i Aspabs verksamhet.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Produkter ska levereras i rätt tid och produceras med en målsättning mot noll fel.
                </p>
              </div>

              {/* Principer */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Vi uppnår detta genom att
                </h2>
                <div className="mt-6 space-y-4">
                  {principles.map((item, i) => (
                    <div key={i} className="flex gap-4 p-5 bg-gray-50 border border-gray-100 rounded-lg">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                        <item.icon className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div className="self-center">
                        <h3 className="font-heading text-base font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Koncept highlight */}
              <div className="border-t border-gray-200 pt-8">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 text-center">
                  <p className="font-heading text-xl font-bold text-[#2563EB]">Rätt från mig!</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Vårt kvalitetskoncept – varje medarbetare tar ansvar för kvaliteten i sitt arbete.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
